import { useState } from 'react'
import { Overlay } from './Overlay'
import { CONTACT_EMAIL, DONATE_URL, DONATION_EUR } from '../../config'

interface DonateDialogProps {
  readonly onClose: () => void
}

/** How the full version is installed once the link arrives. */
const INSTALL_STEPS: readonly { readonly platform: string; readonly how: string }[] = [
  {
    platform: 'macOS und Windows',
    how: 'In Chrome oder Edge öffnen, dann das Installationssymbol rechts in der Adressleiste — oder Menü → „multitimbral installieren". Danach liegt sie als eigenes Fenster im Dock bzw. im Startmenü.',
  },
  {
    platform: 'iPhone und iPad',
    how: 'In Safari öffnen, Teilen-Symbol antippen, „Zum Home-Bildschirm". Startet ohne Browserleisten im Hochformat.',
  },
  {
    platform: 'Android',
    how: 'In Chrome öffnen, Menü → „App installieren".',
  },
]

/**
 * The support panel.
 *
 * Deliberately manual: verifying a payment needs an integration, and at five
 * euros a handful of times a week an inbox is cheaper and more honest than
 * pretending an automatic check exists.
 */
export function DonateDialog({ onClose }: DonateDialogProps): React.JSX.Element {
  const [email, setEmail] = useState('')

  const body = [
    'Hallo,',
    '',
    `ich habe ${String(DONATION_EUR)} € für multitimbral überwiesen.`,
    email.trim() === '' ? '' : `Zugang bitte an: ${email.trim()}`,
    '',
    'Viele Grüße',
  ]
    .filter((line, index, all) => line !== '' || all[index - 1] !== '')
    .join('\n')

  const mailto =
    CONTACT_EMAIL === ''
      ? null
      : `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('multitimbral — Vollversion')}` +
        `&body=${encodeURIComponent(body)}`

  return (
    <Overlay title="Projekt unterstützen" onClose={onClose}>
      <p className="overlay__text">
        multitimbral ist in ein paar Wochenenden entstanden und läuft ohne Werbung, ohne Tracking
        und ohne dass irgendein Ton den Browser verlässt. Wer mag, unterstützt das mit{' '}
        <strong>{DONATION_EUR} €</strong> — und bekommt dafür den Zugang zur Vollversion.
      </p>

      <ol className="overlay__steps">
        <li>
          {DONATE_URL === '' ? (
            <>Überweise {DONATION_EUR} € per PayPal.</>
          ) : (
            <>
              <a className="button overlay__pay" href={DONATE_URL} target="_blank" rel="noreferrer">
                {DONATION_EUR} € per PayPal
              </a>
            </>
          )}
        </li>
        {/* Without an address to write to, the field would collect something
            with nowhere to go — the transfer note does the same job. */}
        {mailto === null ? (
          <li>
            Schreib deine E-Mail-Adresse in den Verwendungszweck der Überweisung. Dorthin geht der
            Zugangsschlüssel.
          </li>
        ) : (
          <li>
            <label className="controls__field">
              <span>E-Mail für den Zugang</span>
              <input
                type="email"
                value={email}
                autoComplete="email"
                aria-label="E-Mail für den Zugang"
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
              />
            </label>
            <a className="button" href={mailto}>
              Adresse per Mail schicken
            </a>
          </li>
        )}
        <li>Du bekommst den Zugangsschlüssel zurück — eintragen unter „Vollversion" im Menü.</li>
      </ol>

      <h3 className="overlay__subtitle">Als App installieren</h3>
      <p className="overlay__text">
        Die Vollversion ist dieselbe App und lässt sich auf allen vier Systemen wie eine native
        installieren — offline lauffähig, eigenes Icon, keine Browserleisten.
      </p>
      <dl className="overlay__install">
        {INSTALL_STEPS.map((step) => (
          <div key={step.platform}>
            <dt>{step.platform}</dt>
            <dd>{step.how}</dd>
          </div>
        ))}
      </dl>
    </Overlay>
  )
}
